import { BaseComponent } from '../../lib/BaseComponent.js';
import { api } from '../../lib/api.js';
import { router } from '../../lib/router.js';
import { authStyles } from './authStyles.js';

/**
 * Accept Invitation Page - sets name + password for an invited user and signs them in.
 */
export class AcceptInvitationPage extends BaseComponent {
  private isLoading = false;
  private errorMessage = '';

  private get token(): string {
    return new URLSearchParams(window.location.search).get('token') || '';
  }

  protected render(): void {
    this.addStyles(this.getCommonStyles());
    this.addStyles(authStyles());

    const container = this.createElement('div', { className: 'auth-container' });
    const header = this.createElement('div', { className: 'auth-header' });
    const title = this.createElement('h1');
    title.appendChild(this.createElement('a', { href: '/' }, 'FireGuard'));
    header.appendChild(title);
    header.appendChild(this.createElement('p', { className: 'subtitle' }, 'Join your team'));
    container.appendChild(header);

    const card = this.createElement('div', { className: 'auth-card' });
    card.appendChild(this.createElement('h2', {}, 'Accept your invitation'));

    if (!this.token) {
      card.appendChild(this.createElement('div', { className: 'error-message' }, 'This invitation link is missing its token. Ask your administrator to resend it.'));
    } else {
      card.appendChild(this.createElement('p', { className: 'help' }, 'Set your name and a password to finish joining.'));
      if (this.errorMessage) card.appendChild(this.createElement('div', { className: 'error-message' }, this.errorMessage));
      const form = this.createElement('form', { className: 'auth-form' });
      form.addEventListener('submit', (e) => this.handleSubmit(e));

      const g0 = this.createElement('div', { className: 'form-group' });
      g0.appendChild(this.createElement('label', { for: 'name' }, 'Your name'));
      g0.appendChild(this.createElement('input', { type: 'text', id: 'name', name: 'name', required: 'true', autocomplete: 'name', placeholder: 'Jordan Alvarez' }));
      form.appendChild(g0);

      const g1 = this.createElement('div', { className: 'form-group' });
      g1.appendChild(this.createElement('label', { for: 'password' }, 'Password'));
      g1.appendChild(this.createElement('input', { type: 'password', id: 'password', name: 'password', required: 'true', minlength: '8', autocomplete: 'new-password' }));
      g1.appendChild(this.createElement('span', { className: 'hint' }, 'At least 8 characters with a mix of letters and numbers.'));
      form.appendChild(g1);

      const g2 = this.createElement('div', { className: 'form-group' });
      g2.appendChild(this.createElement('label', { for: 'confirm' }, 'Confirm password'));
      g2.appendChild(this.createElement('input', { type: 'password', id: 'confirm', name: 'confirm', required: 'true', autocomplete: 'new-password' }));
      form.appendChild(g2);

      const btn = this.createElement('button', { type: 'submit', className: 'btn-primary' }, this.isLoading ? 'Joining…' : 'Join team');
      if (this.isLoading) btn.setAttribute('disabled', 'true');
      form.appendChild(btn);
      card.appendChild(form);
    }

    const footer = this.createElement('div', { className: 'auth-footer' });
    const p = this.createElement('p', {}, 'Already have an account? ');
    p.appendChild(this.createElement('a', { href: '/login' }, 'Sign in'));
    footer.appendChild(p);
    card.appendChild(footer);

    container.appendChild(card);
    this.shadow.appendChild(container);
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    const data = new FormData(e.target as HTMLFormElement);
    const name = ((data.get('name') as string) || '').trim();
    const password = (data.get('password') as string) || '';
    const confirm = (data.get('confirm') as string) || '';
    if (password !== confirm) {
      this.errorMessage = 'Passwords do not match.';
      this.rerender();
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';
    this.rerender();
    try {
      const res = await api.acceptInvitation({ token: this.token, password, name });
      if (res?.token) {
        api.setToken(res.token);
        router.navigate('/dashboard');
        return;
      }
      router.navigate('/login');
    } catch (error) {
      this.errorMessage = (error as Error).message || 'Could not accept invitation';
      this.isLoading = false;
      this.rerender();
    }
  }

  private rerender(): void {
    this.shadow.replaceChildren();
    this.render();
  }
}

customElements.define('accept-invitation-page', AcceptInvitationPage);
